import { z } from 'zod';

import {
  granularityLevelSchema,
  ingestionContextV1Schema,
  ingestionContextV2Schema,
  paginationSchema,
} from '../shared/schemes.js';

export const DataFileStatusSchema = z.enum([
  'draft',
  'uncompleted',
  'uploading',
  'validating',
  'loading',
  'uploaded',
  'overwritten',
  'failed',
  'deleting',
  'archived',
]);

export type DataFileStatus = z.infer<typeof DataFileStatusSchema>;

export const CommonDataFileItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  platforms: z.array(z.string()).optional(),
  importType: z.union([z.literal('auto'), z.literal('manual')]),
  granularityLevel: granularityLevelSchema,
  importDate: z.string(),
  createdDate: z.string(),
  status: DataFileStatusSchema,
  sender: z.string().optional(),
  fromTemplate: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  previousStatus: DataFileStatusSchema.nullable().optional(),
  scheduledArchiving: z.string().nullable().optional(),
  scheduledDeleting: z.string().nullable().optional(),
});

export const TableDataFilesQueryCommonSchema = z.object({
  search: z.string().prefault(''),
  pageNumber: z.number().prefault(1),
  itemsPerPage: z.number().prefault(50),
  sortBy: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  platform: z.array(z.string()).optional(),
  importType: z
    .union([
      z.literal('auto'),
      z.literal('manual'),
      z.array(z.union([z.literal('auto'), z.literal('manual')])),
    ])
    .optional(),
  template: z.array(z.string()).optional(),
  sender: z.array(z.string()).optional(),
  status: z.array(DataFileStatusSchema).optional(),
  granularityLevel: z.array(granularityLevelSchema).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  includeHidden: z.boolean().optional(),
});

export const DataFileItemV1Schema = CommonDataFileItemSchema.extend({
  campaignName: z.string().nullable(),
});

export const DataFileItemV2Schema = CommonDataFileItemSchema.extend({
  activityName: z.string().nullable(),
});

export type TableDataFileItem =
  | z.infer<typeof DataFileItemV1Schema>
  | z.infer<typeof DataFileItemV2Schema>;

export const TableDataFilesV1QuerySchema =
  TableDataFilesQueryCommonSchema.extend({
    campaign: z.array(z.string()).optional(),
  });

export const TableDataFilesV2QuerySchema =
  TableDataFilesQueryCommonSchema.extend({
    activity: z.array(z.string()).optional(),
  });

export const DataFilesV1ResponseSchema = z.object({
  data: z
    .object({
      items: z.array(DataFileItemV1Schema),
      additional: z.object({}).nullable(),
    })
    .extend(paginationSchema.shape),
});

export type DataFilesListResponse =
  | DataFilesListV1Response
  | DataFilesListV2Response;

export type DataFilesListV1Response = z.infer<typeof DataFilesV1ResponseSchema>;
export type DataFilesV1QueryResult = DataFilesListV1Response['data'];

export const TableDataFilesV2ResponseSchema = z.object({
  data: z
    .object({
      items: z.array(DataFileItemV2Schema),
      additional: z.object({}).nullable(),
    })
    .extend(paginationSchema.shape),
});

export type DataFilesListV2Response = z.infer<
  typeof TableDataFilesV2ResponseSchema
>;
export type TableDataFilesV2QueryResult = DataFilesListV2Response['data'];

export const CreateDataFileRequestSchema = z.object({
  granularityLevel: granularityLevelSchema.optional(),
  fromTemplateId: z.string().nullable(),
});

export const CreateDataFileResponseSchema = z.object({
  data: z.object({
    additional: z.object({
      id: z.string(),
    }),
  }),
});

export type CreateDataFileRequest = z.infer<typeof CreateDataFileRequestSchema>;
export type CreateDataFileResponse = z.infer<
  typeof CreateDataFileResponseSchema
>;

export const TableDataFilePreviewCommonSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  sheetName: z.string().nullable(),
  currentStep: z.enum([
    'upload',
    'headers_mapping',
    'validation',
    'channel_assignment',
    'success',
  ]),
  status: DataFileStatusSchema,
  granularityLevel: granularityLevelSchema,
  isFileUploaded: z.boolean(),
  isEdited: z.boolean(),
  fromTemplate: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
  templateHeadersMismatched: z.boolean().nullable(),
  templateSheetsMismatched: z.boolean().nullable(),
  footerRowsCount: z.number().min(0).max(40).nullable().optional(),
  hasDefaultBudgetingStructure: z.boolean(),
  error: z
    .enum([
      'file_not_attached',
      'file_is_empty',
      'multiple_files_attached',
      'file_format_incorrect',
      'file_size_exceeded',
      'domain_error',
      'unknown_error',
    ])
    .nullable(),
  parsingError: z
    .enum([
      'CellSizeExceedError',
      'ColumnsExceedError',
      'EmptyFileError',
      'RowExceedError',
      'TooManySheetsError',
    ])
    .nullable()
    .optional(),
});

export const TableDataFilePreviewV1Schema =
  TableDataFilePreviewCommonSchema.extend({
    context: ingestionContextV1Schema,
  });

export const TableDataFilePreviewV2Schema =
  TableDataFilePreviewCommonSchema.extend({
    context: ingestionContextV2Schema,
  });

export const DataFilePreviewV1ResponseSchema = z.object({
  data: z.object({
    items: z.array(TableDataFilePreviewV1Schema),
  }),
});

export const DataFilePreviewV2ResponseSchema = z.object({
  data: z.object({
    items: z.array(TableDataFilePreviewV2Schema),
  }),
});

export type TableDataFilePreviewResponse = z.infer<
  typeof TableDataFilePreviewCommonSchema
>;
export type TableDataFilePreviewV1Response = z.infer<
  typeof DataFilePreviewV1ResponseSchema
>;
export type TableDataFilePreviewV2Response = z.infer<
  typeof DataFilePreviewV2ResponseSchema
>;

export type TableDataFilePreviewQueryResult =
  | TableDataFilePreviewV1QueryResult
  | TableDataFilePreviewV2QueryResult;
export type TableDataFilePreviewV1QueryResult =
  TableDataFilePreviewV1Response['data']['items'][0];
export type TableDataFilePreviewV2QueryResult =
  TableDataFilePreviewV2Response['data']['items'][0];

export const UpdateContextRequestSchema = z.object({
  context: z.object({
    mediaPlanId: z.string().nullable().optional(),
    mediaPlanName: z.string().nullable().optional(),
    activityId: z.string().nullable().optional(),
    activityName: z.string().nullable().optional(),
    journeyId: z.number().nullable().optional(),
    journeyName: z.string().nullable().optional(),
    platformName: z.string().nullable().optional(),
  }),
});

export const DataFileSheetNamesSchema = z.object({
  data: z.object({
    items: z.array(z.object({ sheets: z.array(z.string()) })),
  }),
});

export type DataFileSheetNamesResponse = z.infer<
  typeof DataFileSheetNamesSchema
>;
export type DataFileSheetNamesQueryResult =
  DataFileSheetNamesResponse['data']['items'][0]['sheets'];

export const SetDataFileSheetNamePayloadSchema = z.object({
  sheetName: z.string(),
});

export type SetDataFileSheetNamePayload = z.infer<
  typeof SetDataFileSheetNamePayloadSchema
>;

export const DataFilePreviewRowsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        id: z.string(),
        cells: z.array(z.string()),
        isHeader: z.boolean(),
      }),
    ),
  }),
});

export type PreviewRowsResponse = z.infer<
  typeof DataFilePreviewRowsResponseSchema
>;
export type PreviewRowsQueryResult = PreviewRowsResponse['data'];

export const TableDataFileFooterRowsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        id: z.string(),
        cells: z.array(z.string()),
      }),
    ),
  }),
});

export type FooterRowsResponse = z.infer<
  typeof TableDataFileFooterRowsResponseSchema
>;
export type TableDataFileFooterRowsQueryResult = FooterRowsResponse['data'];

export const SetFooterRowsCountPayloadSchema = z.object({
  footerRowsCount: z.number().min(0).max(40),
});

export type SetFooterRowsCountPayload = z.infer<
  typeof SetFooterRowsCountPayloadSchema
>;

export const HeaderMappingSchema = z.object({
  position: z.number(),
  rawHeader: z.string(),
  mappedHeader: z.string().nullable(),
});

export const HeadersMappingSchema = z.object({
  data: z.object({
    items: z.array(HeaderMappingSchema),
    additional: z.object({
      canBeMapped: z.boolean(),
      maxCount: z.number(),
    }),
  }),
});

export type HeaderMapping = z.infer<typeof HeaderMappingSchema>;
export type HeadersMappingResponse = z.infer<typeof HeadersMappingSchema>;
export type HeadersMappingQueryResult = HeadersMappingResponse['data'];

export const DataFileFormatsSchema = z.object({
  numberFormat: z.string().nullable(),
  dateOrder: z.string().nullable(),
});

export const DatafileFormatsResponseSchema = z.object({
  data: z.object({
    items: z.array(DataFileFormatsSchema),
  }),
});

export type DataFileFormats = z.infer<typeof DataFileFormatsSchema>;
export type DataFileFormatsResponse = z.infer<
  typeof DatafileFormatsResponseSchema
>;
export type DataFileFormatsQueryResult =
  DataFileFormatsResponse['data']['items'][0];

export const DataFileFilterOptionsQuerySchema = z.object({
  include: z.array(z.string()),
});

export const DataFileFilterOptionsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        filterType: z.string(),
        options: z.array(
          z.object({
            key: z.string(),
            value: z.string(),
          }),
        ),
      }),
    ),
  }),
});

export type DataFileFilterOptionsResponse = z.infer<
  typeof DataFileFilterOptionsResponseSchema
>;
export type DataFileFilterOptionsQueryResult =
  DataFileFilterOptionsResponse['data']['items'][0];

export const HeadersFormatsOptionsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        numberFormats: z.array(z.string()),
        dateOrders: z.array(z.string()),
      }),
    ),
  }),
});

export type HeadersFormatsOptionsResponse = z.infer<
  typeof HeadersFormatsOptionsResponseSchema
>;
export type HeadersFormatsOptionsQueryResult =
  HeadersFormatsOptionsResponse['data']['items'][number];
